import React, { useEffect, useState, useContext, useMemo } from 'react';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useMutation, gql } from '@apollo/client';
import {
    Typography,
    Grid,
    Button,
    CircularProgress,
    createTheme,
    ThemeProvider,
    Dialog,
    DialogActions,
    DialogTitle,
} from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import { css } from '@emotion/react';

import Image from 'react-bootstrap/Image';
import FileDropZone from './FileDropZone';
import * as Sentry from '@sentry/react';
import { UserContext } from '../App';
import track from '../../utilities/track';
import getOrdersFilesAsPDF from '../../utilities/getOrdersFilesAsPDF';
import getOrdersPhotosAsZip from '../../utilities/getOrdersPhotosAsZip';
import { OnwardCard } from './Card';

import { FileEarmarkPdf } from 'react-bootstrap-icons';
import uploadPhotoAsync from '../../utilities/uploadPhotoAsync';
import { saveAs } from 'file-saver';
import parseJson from '../../utilities/stringJsonParser';
import { Header2 } from './Crossdocking/blocks';
import ImageCoursel from './ImageCarousel';
import { PrimaryButton } from '@/styles/blocks';

const UPDATE_ORDER_FILES = gql`
    mutation UpdateOrderFiles(
        $order_id: uuid
        $pod: jsonb
        $bol_file: jsonb
        $warehouse_file: jsonb
        $delivery_photo: jsonb
        $proof_of_pickup: jsonb
        $waybill: jsonb
        $pickup_notification: jsonb
        $middle_mile_pod: jsonb
    ) {
        update_orders(
            where: { order_id: { _eq: $order_id } }
            _set: {
                pod: $pod
                bol_file: $bol_file
                warehouse_file: $warehouse_file
                delivery_photo: $delivery_photo
                proof_of_pickup: $proof_of_pickup
                waybill: $waybill
                pickup_notification: $pickup_notification
                middle_mile_pod: $middle_mile_pod
            }
        ) {
            returning {
                order_id
                order_number
                pod
                bol_file
                warehouse_file
                delivery_photo
                proof_of_pickup
                waybill
                pickup_notification
                middle_mile_pod
            }
        }
    }
`;

function UploadedFilesCard(props) {
    const { order, category, userType, editable, loadOrder } = props;
    const [fileCategory, setFileCategory] = useState('');
    const [fileArray, setFileArray] = useState([]);
    const [fileArrayWithTypes, setFileArrayWithTypes] = useState([]);
    const [fileIndex, setFileIndex] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { user: userState } = useContext(UserContext);
    const [updateOrderFiles, { loading, error }] = useMutation(UPDATE_ORDER_FILES);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [viewingImages, setViewingImages] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleClickOpen = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
    };

    const authenticateFiles = async () => {
        let files = [];
        switch (category) {
            case 'Warehouse':
                setFileCategory('warehouse_file');
                if (typeof order.warehouse_file === 'string' && order.warehouse_file !== '') {
                    files = [order.warehouse_file];
                } else {
                    files = order.warehouse_file;
                }
                break;
            case 'POD':
                setFileCategory('pod');
                if (typeof order.pod === 'string' && order.pod !== '') {
                    files = [order.pod];
                } else {
                    files = order.pod;
                }
                break;
            case 'Middle Mile POD':
                setFileCategory('middle_mile_pod');
                if (typeof order.middle_mile_pod === 'string' && order.middle_mile_pod !== '') {
                    files = [order.middle_mile_pod];
                } else {
                    files = order.middle_mile_pod;
                }
                break;
            case 'BOL':
                setFileCategory('bol_file');
                if (typeof order.bol_file === 'string' && order.bol_file !== '') {
                    files = [order.bol_file];
                } else {
                    files = order.bol_file;
                }
                break;
            case 'Final Delivery Photos':
                setFileCategory('delivery_photo');
                if (typeof order.delivery_photo === 'string' && order.delivery_photo !== '') {
                    files = [order.delivery_photo];
                } else {
                    files = order.delivery_photo;
                }
                break;
            case 'Proof of Pickup':
                setFileCategory('proof_of_pickup');
                if (typeof order.proof_of_pickup === 'string' && order.proof_of_pickup !== '') {
                    files = [order.proof_of_pickup];
                } else {
                    files = order.proof_of_pickup;
                }
                break;
            case 'Waybill':
                setFileCategory('waybill');
                if (typeof order.waybill === 'string' && order.waybill !== '') {
                    files = [order.waybill];
                } else {
                    files = order.waybill;
                }
                break;
            case 'Pickup Notification':
                setFileCategory('pickup_notification');
                if (typeof order.pickup_notification === 'string' && order.pickup_notification !== '') {
                    files = [order.pickup_notification];
                } else {
                    files = order.pickup_notification;
                }
                break;
            case 'Item Labels':
                setFileCategory('item_labels');
                if (typeof order.item_labels === 'string' && order.item_labels !== '') {
                    files = [order.item_labels];
                } else {
                    files = order.item_labels;
                }
                break;
        }

        const refs = (
            await Promise.all(
                (files || []).map(async (file) => {
                    try {
                        let url = null;
                        let rest = {};
                        if (typeof file === 'string') {
                            url = file;
                        } else {
                            url = file.url;
                            rest = file;
                        }
                        const reference = ref(getStorage(), url.split('?')[0]);
                        const authURL = await getDownloadURL(reference);
                        return { ...rest, url: authURL };
                    } catch (e) {
                        console.error(e);
                        return null;
                    }
                })
            )
        ).filter((x) => !!x);

        setFileArray(refs);
    };

    useEffect(() => {
        authenticateFiles();
    }, [order]);

    useEffect(() => {
        if (fileArray) {
            addFileTypes();
        }
    }, [fileArray]);

    const handleFileDelete = async () => {
        try {
            let updatedFileArray = [...fileArray];
            updatedFileArray.splice(fileIndex, 1);

            await updateOrderFiles({
                variables: {
                    ...order,
                    [fileCategory]: updatedFileArray,
                },
            });
            setFileArray(updatedFileArray);
            setFileIndex(null);

            if (loadOrder) {
                loadOrder();
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);
        }
        handleClose();
    };

    const handleFileAdd = async (filesArray) => {
        const uploadedFiles = [];
        setUploading(true);

        try {
            for (const { file, fileType } of filesArray) {
                const fileUrl = URL.createObjectURL(file);
                const fbFileUrl = await uploadPhotoAsync(fileUrl, fileCategory, fileType);
                const fbFile = ['POD', 'Middle Mile POD', 'Final Delivery Photos', 'Proof of Pickup'].includes(category)
                    ? {
                          url: fbFileUrl,
                          environment: 'web',
                      }
                    : fbFileUrl;

                uploadedFiles.push(fbFile);

                track(`${userType} uploaded a file`, {
                    order_number: order.order_number,
                    type: category,
                    userEmail: userState.email,
                    environmentType: 'web',
                    test_acc: (userState.shipper || userState.carrier)?.test_acc,
                });
            }

            const updatedFileArray = fileArray ? [...fileArray, ...uploadedFiles] : [...uploadedFiles];
            await updateOrderFiles({
                variables: {
                    ...order,
                    [fileCategory]: updatedFileArray,
                },
            });
            setFileArray(updatedFileArray);
            if (loadOrder) {
                loadOrder();
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const addFileTypes = async () => {
        let resultArray = [];

        for (const file of fileArray) {
            let type = await checkType(file);
            resultArray.push([file, type]);
        }

        setFileArrayWithTypes(resultArray);
    };

    const checkType = async (fileSource) => {
        if (fileSource.url && fileSource.url.includes('.pdf')) {
            return 'pdf';
        }

        try {
            const res = await fetch(fileSource.url ? fileSource.url : fileSource);
            const blob = await res.blob();

            if (blob.type === 'application/pdf') {
                return 'pdf';
            } else if (blob.type === 'image/jpeg') {
                return 'jpg';
            } else if (blob.type === 'image/png') {
                return 'png';
            } else {
                return blob.type;
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error(err);
        }
    };

    function ConfirmDeleteDialog() {
        return (
            <div>
                <Dialog
                    open={dialogOpen}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{'Are you sure you want to delete this file?'}</DialogTitle>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleFileDelete} autoFocus>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }

    async function handleDownload() {
        if (category === 'Final Delivery Photos') {
            const zip = await getOrdersPhotosAsZip(order);
            if (zip) {
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                saveAs(zipBlob, `${order.order_number ? order.order_number + ' - ' : ''}Final Delivery Photos.zip`);
            } else {
                const error = new Error(
                    `Could not successfully retrieve Final Delivery Photo zip file for order ${order.order_number}`
                );
                Sentry.captureException(error);
                console.error(error);
            }
        } else {
            const pdfDoc = await getOrdersFilesAsPDF(order, fileCategory);

            if (pdfDoc) {
                saveAs(await pdfDoc.saveAsBase64({ dataUri: true }), order.order_number + ' - ' + category);
            }
        }
    }

    const theme = createTheme({
        palette: {
            text: {
                secondary: '#4c4c4c',
            },
            primary: {
                main: '#59b863',
                contrastText: '#fff',
            },
            secondary: {
                main: '#4c4c4c',
                contrastText: '#fff',
            },
        },
        typography: {
            fontFamily: ['Montserrat', 'Roboto', 'Arial'].join(','),
            h6: {
                color: '#8d8d8d',
                fontWeight: '850',
            },
        },
    });

    return (
        <>
            <ThemeProvider theme={theme}>
                <OnwardCard
                    css={css`
                        margin-top: 2.5rem;
                    `}
                >
                    <div className="d-flex flex-row align-items-center">
                        <Header2
                            variant="h6"
                            color="secondary"
                            css={css`
                                margin-bottom: 10px;
                            `}
                        >
                            {category}
                        </Header2>
                    </div>
                    <Grid container direction="column">
                        <Grid container spacing={1}>
                            {fileArrayWithTypes &&
                                fileArrayWithTypes.map((file, i) => {
                                    const fileUrl = file[0]?.hasOwnProperty('url') ? file[0].url : file[0];
                                    if (file[1] === 'pdf') {
                                        return (
                                            <Grid item key={i} className="d-flex position-relative">
                                                {editable && (
                                                    <div
                                                        onClick={() => {
                                                            setFileIndex(i);
                                                            handleClickOpen();
                                                        }}
                                                        style={{
                                                            backgroundColor: 'black',
                                                            cursor: 'pointer',
                                                            opacity: '0.6',
                                                            height: '25px',
                                                            width: '25px',
                                                            position: 'absolute',
                                                            top: '5px',
                                                            left: '5px',
                                                            borderRadius: '50%',
                                                            zIndex: 2,
                                                        }}
                                                    >
                                                        <Clear
                                                            style={{
                                                                position: 'relative',
                                                                zIndex: 3,
                                                                top: '-2px',
                                                                left: '1px',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <a href={fileUrl} target="_blank">
                                                    <FileEarmarkPdf size={100} />
                                                </a>
                                            </Grid>
                                        );
                                    } else
                                        return (
                                            <Grid item key={i} className="d-flex position-relative">
                                                {editable && (
                                                    <div
                                                        onClick={() => {
                                                            setFileIndex(i);
                                                            handleClickOpen();
                                                        }}
                                                        style={{
                                                            backgroundColor: 'black',
                                                            cursor: 'pointer',
                                                            opacity: '0.6',
                                                            height: '25px',
                                                            width: '25px',
                                                            position: 'absolute',
                                                            top: '5px',
                                                            right: '5px',
                                                            borderRadius: '50%',
                                                            zIndex: 2,
                                                        }}
                                                    >
                                                        <Clear
                                                            style={{
                                                                position: 'relative',
                                                                zIndex: 3,
                                                                top: '-2px',
                                                                left: '1px',
                                                                color: 'white',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <Image
                                                    style={{
                                                        height: '100px',
                                                        width: '100px',
                                                    }}
                                                    src={fileUrl}
                                                    onClick={() => {
                                                        setSelectedImageIndex(i);
                                                        setViewingImages(true);
                                                    }}
                                                    css={css`
                                                        cursor: pointer;
                                                    `}
                                                />
                                            </Grid>
                                        );
                                })}

                            {editable && (
                                <Grid item className="d-flex position-relative">
                                    {uploading && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                height: '100px',
                                                width: '100px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                borderRadius: '4px',
                                                border: '1px solid #9d9d9d',
                                                zIndex: 1,
                                            }}
                                        >
                                            <CircularProgress size={32} />
                                            <Typography variant="body2" style={{ marginTop: 8, fontSize: 12 }}>
                                                Uploading...
                                            </Typography>
                                        </div>
                                    )}
                                    <FileDropZone handleFileAdd={handleFileAdd} type="multi" disabled={uploading} />
                                </Grid>
                            )}

                            {!editable && fileArrayWithTypes.length < 1 && (
                                <Grid item className="d-flex">
                                    <Typography variant="body2">No files uploaded yet</Typography>
                                </Grid>
                            )}
                        </Grid>

                        {fileArrayWithTypes.length > 0 && (
                            <Grid container className="mt-3">
                                <Grid item>
                                    <PrimaryButton
                                        style={{
                                            padding: '5px',
                                        }}
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        onClick={handleDownload}
                                        disabled={!fileArray || fileArray.length === 0}
                                    >
                                        Download
                                    </PrimaryButton>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </OnwardCard>
                <ConfirmDeleteDialog />
            </ThemeProvider>
            <ImageCoursel
                images={fileArray}
                initialIndex={selectedImageIndex}
                open={viewingImages}
                onClose={() => {
                    setViewingImages(false);
                    setSelectedImageIndex(null);
                }}
            />
        </>
    );
}

export default UploadedFilesCard;
